// The Personalized and Task activity forms. A task is a personalized banner with a button
// and a completion rule, so the two share one composer: audience, copy, style and image.
// It lives above the tabs, so what is typed survives moving between them.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import api, { uploadAPI } from '../../services/api';
import { useBannerAudience } from './useBannerAudience';
import { useActionsRelease } from './useActionsRelease';
import { useFillForm } from './useFillForm';
import { useTaskButtons } from './useTaskButtons';
import { useReachLevel } from './useReachLevel';
import { fillForPreview, tokenProblem, tokensAsLabels } from './bannerTokens';
import type { AfterDue } from './bannerApi';
import {
  ACCENT_PRESETS,
  DEFAULT_TASK_DAYS,
  DURATIONS,
  taskTypesFor,
  type BannerTab,
  type DecorationType,
  type TaskDef,
  type TaskPick,
  type TaskType,
} from './bannerOptions';
import type { PushLogEntry } from './pushLog';

export function useBannerComposer({
  tab,
  sending,
  setSending,
  report,
  onSent,
}: {
  tab: BannerTab;
  sending: boolean;
  setSending: (on: boolean) => void;
  /** A send's outcome: into the log, and shown on the tab it came from. */
  report: (entry: PushLogEntry, from: BannerTab) => void;
  /** A send went through: Sent history reads the list again, so it never lags behind. */
  onSent: () => void;
}) {
  const isTask = tab === 'task';
  const isBannerComposer = tab === 'personalized' || isTask;

  // Sent history reads stored text too, so a detail in it needs its name there as well.
  const audience = useBannerAudience(isBannerComposer, isBannerComposer || tab === 'history');
  const { selectedProjectId, recipientMode, selectedEmpCodes, selectedManagerCodes, recipientCount, hasAudience, details } = audience;

  const [annTitle, setAnnTitle] = useState('');
  const [annBody, setAnnBody] = useState('');
  const [annAccent, setAnnAccent] = useState(ACCENT_PRESETS[0].value);
  // A message starts with Sparkles, as it always has; a task with none, which suits a to-do.
  const [messageDecoration, setMessageDecoration] = useState<DecorationType>('sparkles');
  const [taskDecoration, setTaskDecoration] = useState<DecorationType>('none');
  const [annVisibleDays, setAnnVisibleDays] = useState(1);
  const [annCountdownTo, setAnnCountdownTo] = useState('');
  // ── Task activity ── shares everything above; these are the task-only extras.
  const [taskType, setTaskType] = useState<TaskType>('resume_upload');
  const [taskSendPush, setTaskSendPush] = useState(true);
  // By when: the form's value ('' for none), and what happens after it.
  const [completeBy, setCompleteBy] = useState('');
  const [afterDue, setAfterDue] = useState<AfterDue>('take_off');
  // Every title and message this page has filled in by itself. Text still equal to
  // one of them is untouched, so a new task, score or month may replace it.
  const autoCopy = useRef(new Set<string>());
  // Drives the live preview pill. Only ticks while a countdown is actually set
  // and the Personalized form is open, so the page is idle the rest of the time.
  const [, setClockTick] = useState(0);
  const [imageBlobPath, setImageBlobPath] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // A new tab or kind asks again, so a page left open catches up with the stores.
  const release = useActionsRelease({ on: isTask, again: taskType });
  const taskTypes = taskTypesFor(release.released);
  const baseDef = taskTypes.find((t) => t.value === taskType) ?? taskTypes[0];
  const isLevelTask = isTask && taskType === 'reach_level';
  const isFormTask = isTask && taskType === 'fill_form';
  const annDecoration = isTask ? taskDecoration : messageDecoration;
  const setAnnDecoration = isTask ? setTaskDecoration : setMessageDecoration;

  // What the task's settings have picked, for its default copy.
  const pickRef = useRef<TaskPick>({ scoreName: null, month: '', formName: null });

  /**
   * Fill the title and message with a task's default copy — but only where the
   * field is empty or still holds copy this page filled in. Anything the admin
   * typed stays exactly as typed.
   */
  const writeTaskCopy = (next: TaskDef) => {
    const copy = next.defaultCopy(pickRef.current);
    const untouched = (prev: string) => !prev.trim() || autoCopy.current.has(prev);
    setAnnTitle((prev) => (untouched(prev) ? copy.title : prev));
    setAnnBody((prev) => (untouched(prev) ? copy.body : prev));
    autoCopy.current.add(copy.title);
    autoCopy.current.add(copy.body);
  };

  const level = useReachLevel({
    on: isLevelTask,
    projectId: selectedProjectId,
    hasAudience,
    audience: audience.audience,
    details,
    onPick: (pick) => {
      pickRef.current = { ...pickRef.current, ...pick };
      writeTaskCopy(baseDef);
    },
  });
  const form = useFillForm({
    on: isFormTask,
    projectId: selectedProjectId,
    onPick: (formName) => {
      pickRef.current = { ...pickRef.current, formName };
      writeTaskCopy(baseDef);
    },
  });

  // A text field has nothing to reach, only something to fill in, so its words say so.
  const hasValue = isLevelTask && level.hasValue;
  const taskDef: TaskDef = hasValue
    ? {
        ...baseDef,
        description: "Stays on each person's home page until it has a value.",
        pushHint: 'Skips people who have already filled it in.',
        alreadyDone: 'already filled in',
        appNote: () =>
          'People who have already filled it in get no card and no push. The app shows it as a plain banner, with no button.',
      }
    : baseDef;

  const buttons = useTaskButtons({
    taskType: isTask ? taskType : null,
    ctaLabel: taskDef.ctaLabel,
    formBannerOnly: form.picked?.bannerOnly === true,
    formLine: form.line,
    released: release.released,
  });

  // A consent's record keeps its words and has nowhere to keep a picture, so a consent has none.
  // One picked for another kind stays picked, for when they go back to it.
  const imageAllowed = !(isTask && taskType === 'consent');

  // Coming to Task activity by any road (a click, Back, or a link to ?tab=task) fills in
  // the default copy. Leaving it drops copy that is still an untouched task default, so a
  // plain message doesn't go out asking for a resume it has no button for. Coming back
  // refills it.
  const wasTask = useRef(false);
  useLayoutEffect(() => {
    if (isTask && !wasTask.current) {
      writeTaskCopy(baseDef);
    } else if (!isTask && wasTask.current) {
      setAnnTitle((prev) => (autoCopy.current.has(prev) ? '' : prev));
      setAnnBody((prev) => (autoCopy.current.has(prev) ? '' : prev));
    }
    wasTask.current = isTask;
    // Only the move between tabs: the copy reads the task and score as they are then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTask]);

  const selectTaskType = (next: TaskDef) => {
    setTaskType(next.value);
    writeTaskCopy(next);
  };

  const previewCountdown = isTask ? completeBy : tab === 'personalized' ? annCountdownTo : '';
  useEffect(() => {
    if (!previewCountdown) return;
    const id = setInterval(() => setClockTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [previewCountdown]);

  const handleImagePick = useCallback(async (file: File | null) => {
    if (!file) return;
    setUploadError(null);
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      setUploadError('Use a JPG, PNG or WebP image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be under 5 MB.');
      return;
    }
    setUploading(true);
    try {
      // Sanitise the filename before upload: /upload/file stores `originalname`
      // verbatim, and the send endpoint validates the returned path against a
      // strict character set — an unsanitised name would round-trip into a 400.
      const safeName = file.name.replace(/[^A-Za-z0-9._-]/g, '_');
      const safeFile = new File([file], safeName, { type: file.type });
      const res = await uploadAPI.uploadFile(safeFile, 'announcements');
      const blobPath = res.data?.data?.blobPath;
      if (!blobPath) throw new Error('Upload succeeded but returned no path');
      setImageBlobPath(blobPath);
      setImagePreview(URL.createObjectURL(file));
    } catch (err: any) {
      setUploadError(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, []);

  const clearImage = () => {
    setImageBlobPath(null);
    setImagePreview(null);
    setUploadError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /**
   * A countdown DRIVES visibility server-side — the card lives until the countdown
   * lands (plus a day, so the "It's here!" state is seen). So the duration picker isn't
   * just redundant when a countdown is set, it would be actively misleading; it's
   * replaced rather than shown alongside.
   */
  const countdownDrivesVisibility = Boolean(
    tab === 'personalized' && annCountdownTo && !Number.isNaN(new Date(annCountdownTo).getTime())
  );

  // By when, as the server will read it. The API refuses a date past or beyond its limit.
  const dueAt = isTask && completeBy ? new Date(completeBy) : null;
  const dueProblem = !dueAt
    ? null
    : Number.isNaN(dueAt.getTime()) || dueAt.getTime() <= Date.now()
    ? 'Complete by must be in the future.'
    : dueAt.getTime() > Date.now() + DEFAULT_TASK_DAYS * 86400000
    ? `Complete by must be within ${DEFAULT_TASK_DAYS} days.`
    : null;

  // Details the text can hold: target and figure only where a figure exists.
  const allowFigure = isLevelTask && !hasValue;
  const textTokens = details.tokens.filter((t) => allowFigure || !t.figureOnly);
  const textProblem = tokenProblem(
    [annTitle, annBody],
    details.tokens,
    allowFigure,
    hasValue ? 'Target and Your figure need a number to reach. This field is only filled in.' : undefined
  );

  // The preview's text as the app gets it: details filled in, never as typed, and on a task
  // the server's own line first, then the message on the next line.
  const figures = { target: level.example.target, figure: level.example.figure };
  const previewTitle = fillForPreview(annTitle, details.tokens, figures);
  const previewMessage = fillForPreview(annBody.trim(), details.tokens, figures) || 'Message';
  const firstLine = isLevelTask ? level.example.line : buttons.firstLine;
  const previewBody = firstLine ? `${firstLine}\n${previewMessage}` : previewMessage;

  // Only the build with the buttons can answer these, so until it is in both stores they are
  // held, with the server's reason. The server refuses them too. Read this waits only to know.
  const heldReason =
    !isTask || release.released
      ? null
      : taskType === 'consent' || (isFormTask && form.picked?.bannerOnly === true) || (taskType === 'read_this' && release.loading)
      ? release.reason
      : null;

  // Why Send is off, in the order the form asks for things. Shown beside the button.
  const blockedReason =
    (isLevelTask ? level.blocked : isFormTask ? form.missing : buttons.missing) ??
    heldReason ??
    dueProblem ??
    audience.detailsBlocked ??
    textProblem;

  const canSend =
    isBannerComposer &&
    hasAudience &&
    annTitle.trim().length > 0 &&
    annBody.trim().length > 0 &&
    !uploading &&
    !blockedReason;

  const doneWhen = isLevelTask ? level.doneWhen : buttons.doneWhen ?? taskDef.doneWhen;

  const sendLabel = sending
    ? 'Sending — this can take a minute for a large group…'
    : level.reach && level.reach.reach > 0
    ? level.reach.reach < level.reach.of
      ? `Send task to ${level.reach.reach.toLocaleString()} of ${level.reach.of.toLocaleString()} employees`
      : `Send task to ${level.reach.reach.toLocaleString()} ${level.reach.reach === 1 ? 'employee' : 'employees'}`
    : isLevelTask
    ? // No number until the server has counted this audience: the send leaves some people out.
      'Send task'
    : recipientMode === 'project'
    ? `Send ${isTask ? 'task ' : ''}to everyone on this project`
    : recipientMode === 'details'
    ? audience.matchCount
      ? `Send ${isTask ? 'task ' : ''}to ${audience.matchCount.toLocaleString()} ${audience.matchCount === 1 ? 'employee' : 'employees'}`
      : `Send ${isTask ? 'task ' : ''}to matching employees`
    : `Send ${isTask ? 'task ' : ''}to ${recipientCount || 'selected'} ${
        recipientMode === 'employees'
          ? recipientCount === 1
            ? 'employee'
            : 'employees'
          : recipientCount === 1
          ? 'team'
          : 'teams'
      }`;

  // How long a task stays, in words, for the send's outcome.
  const taskStays = () => {
    if (!dueAt) {
      return doneWhen
        ? `Banner stays until ${doneWhen}, for at most ${DEFAULT_TASK_DAYS} days.`
        : `Banner stays for ${DEFAULT_TASK_DAYS} days.`;
    }
    const date = `${dueAt.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    })} IST`;
    if (afterDue === 'keep_showing') {
      return `${doneWhen ? `Banner stays until ${doneWhen}. Due` : 'Banner is due'} ${date}, then it keeps showing, for at most ${DEFAULT_TASK_DAYS} days.`;
    }
    return doneWhen ? `Banner stays until ${doneWhen}, or ${date}, when it comes off.` : `Banner stays until ${date}, then comes off.`;
  };

  const send = async () => {
    if (!canSend) return;
    const from: BannerTab = isTask ? 'task' : 'personalized';
    setSending(true);
    try {
      const res = await api.post('/announcements/send', {
        ...audience.audience,
        projectId: selectedProjectId ?? undefined,
        // Messages don't send kind or sendPush — the API defaults them to
        // 'message' and always-push, which is exactly what they had before.
        ...(isTask ? { kind: 'task', taskType, sendPush: taskSendPush } : {}),
        // Only with their own taskType: the API refuses them on anything else.
        ...(isFormTask && form.settings ? { fillForm: form.settings } : {}),
        ...(isLevelTask && level.settings ? { reachLevel: level.settings } : {}),
        ...buttons.sendBody,
        title: annTitle.trim(),
        body: annBody.trim(),
        imageBlobPath: imageAllowed ? imageBlobPath ?? undefined : undefined,
        accentColor: annAccent,
        decoration: annDecoration,
        // A task's ceiling. With a Complete by date the API works the window out itself.
        visibleDays: isTask ? DEFAULT_TASK_DAYS : annVisibleDays,
        // <input type="datetime-local"> yields local wall time with no zone;
        // let the browser attach the sender's offset before sending. A task
        // has no countdown (the API rejects one), so a value left over from
        // the Personalized form is never sent with it.
        countdownTo:
          !isTask && annCountdownTo ? new Date(annCountdownTo).toISOString() : undefined,
        ...(dueAt ? { completeBy: dueAt.toISOString(), afterDue } : {}),
      });
      const d = res.data;
      const durationLabel = DURATIONS.find((x) => x.days === d.visibleDays)?.label ?? `${d.visibleDays} days`;
      // Reach a level sends only, at the top level of the response: under scoreTarget when
      // the server stored it as today's Reach a score.
      const st = (d.scoreTarget ?? d.reachLevel) as
        | { belowTarget: number; alreadyMet: number; noTarget: number }
        | undefined;
      const parts = [`Sent to ${d.recipients} employee${d.recipients === 1 ? '' : 's'}`];
      if (st) {
        parts.push(
          hasValue ? `${st.belowTarget} not filled in` : `${st.belowTarget} below target`,
          hasValue ? `${st.alreadyMet} already filled in` : `${st.alreadyMet} already met`
        );
      }
      if (isTask && !taskSendPush) {
        parts.push('no push sent (banner only)');
      } else {
        parts.push(`${d.pushesSent} push${d.pushesSent === 1 ? '' : 'es'} delivered`);
        if (d.pushesSkippedNoToken > 0) {
          parts.push(`${d.pushesSkippedNoToken} without a registered device (card still shows)`);
        }
        // Task sends only: people already known to have done it are spared the
        // push. Their banner is still written and completes the moment their
        // app asks for it.
        // For Reach a level that is the "already met" count above.
        if (isTask && d.pushesSkippedAlreadyDone > 0 && !st) {
          parts.push(`${d.pushesSkippedAlreadyDone} not pushed — ${taskDef.alreadyDone}`);
        }
      }
      // Now a {count, sample} object — a project-wide send can drop hundreds
      // and the API no longer returns them all.
      if (d.droppedOffProject?.count) {
        parts.push(`${d.droppedOffProject.count} skipped — not on this project`);
      }
      if (st?.noTarget) {
        parts.push(`${st.noTarget} left out — ${d.scoreTarget ? 'no store category with a target' : 'no target'}`);
      }
      report(
        {
          timestamp: new Date().toLocaleTimeString(),
          empCode:
            recipientMode === 'project'
              ? 'Everyone on this project'
              : recipientMode === 'details'
              ? 'By details'
              : recipientMode === 'employees'
              ? `${selectedEmpCodes.length} employee${selectedEmpCodes.length === 1 ? '' : 's'}`
              : `${selectedManagerCodes.length} team${selectedManagerCodes.length === 1 ? '' : 's'}`,
          type: from,
          taskType: isTask ? taskType : undefined,
          success: d.success,
          message: isTask
            ? `${parts.join(' · ')}. ${taskStays()}`
            : `${parts.join(' · ')}. Card stays on their home for ${durationLabel}.`,
          content: {
            title: tokensAsLabels(annTitle.trim(), details.tokens),
            body: tokensAsLabels(annBody.trim(), details.tokens),
          },
        },
        from
      );
      onSent();
    } catch (err: any) {
      const errMsg = err.response?.data?.message || err.message || 'Request failed';
      report(
        {
          timestamp: new Date().toLocaleTimeString(),
          empCode:
            recipientMode === 'project'
              ? 'Everyone on this project'
              : recipientMode === 'details'
              ? 'By details'
              : `${recipientCount} selected`,
          type: from,
          taskType: isTask ? taskType : undefined,
          success: false,
          message: errMsg,
        },
        from
      );
    } finally {
      setSending(false);
    }
  };

  return {
    isTask,
    isLevelTask,
    audience,
    level,
    form,
    buttons,
    annTitle,
    setAnnTitle,
    annBody,
    setAnnBody,
    annAccent,
    setAnnAccent,
    annDecoration,
    setAnnDecoration,
    annVisibleDays,
    setAnnVisibleDays,
    annCountdownTo,
    setAnnCountdownTo,
    countdownDrivesVisibility,
    taskType,
    taskDef,
    taskTypes,
    release,
    selectTaskType,
    doneWhen,
    taskSendPush,
    setTaskSendPush,
    completeBy,
    setCompleteBy,
    afterDue,
    setAfterDue,
    previewCountdown,
    textTokens,
    imagePreview: imageAllowed ? imagePreview : null,
    imageAllowed,
    uploading,
    uploadError,
    fileInputRef,
    handleImagePick,
    clearImage,
    previewTitle,
    previewBody,
    blockedReason,
    canSend,
    sendLabel,
    send,
  };
}

export type BannerComposer = ReturnType<typeof useBannerComposer>;
